import { registerActions } from "../actionRegistry";
/**
 * Product Images Functions
 *
 * Functions for managing product images, selection, sorting, and defaults.
 */

import { toastSuccess } from "../ui/messages";
import { closeModalLast } from "../ui/modal";
import {
  getDomElement,
  getDomElementOrThrow,
  setHtml,
  addClass,
  removeClass,
  queryAll,
} from "../utils/dom";
import { makeSortable } from "../plugins/sortable";

/**
 * Set product images based on selection
 */
export function setproductimages(aspect: string, mode: string): void {
  let selected = "";
  let selectedhtml = "";

  for (const selEl of queryAll<HTMLInputElement>(".img-selected")) {
    const the_id = selEl.id;
    if (!the_id) continue;

    const raw_id = the_id.replace("img-selected-", "");
    if (selEl.value === "1") {
      if (selected !== "") selected += ",";
      selected += raw_id;
      const imgSrc = getDomElementOrThrow<HTMLImageElement>(`img-selected-src-${raw_id}`).src;
      selectedhtml += `<div class="addproduct-img-selected addproduct-img-selected-${aspect}" id="preview-${raw_id}"><img id="interior-source-${raw_id}" src="${imgSrc}"></div>`;
    }
  }

  if (mode === "multi_custom") {
    getDomElementOrThrow<HTMLInputElement>("custom_interiors").value = selected;
    setHtml("custom_interiors_target", selectedhtml);
    closeModalLast();
    toastSuccess("Custom product images are now set");
  }

  if (mode === "main") {
    getDomElementOrThrow<HTMLInputElement>(`addproduct_images_${aspect}`).value = selected;
    setHtml(`addproduct_images_${aspect}_target`, selectedhtml);
    makeSortable(`addproduct_images_${aspect}_target`, {
      onEnd: function () {
        updateproductimagessort(aspect);
      },
    });

    closeModalLast();
    toastSuccess("Product images are now set");
    setdefaultimage(aspect);
  }

  if (mode === "variant") {
    const imgSelectId = getDomElement<HTMLInputElement>("img-select-id")?.value;
    if (selected !== "") {
      getDomElementOrThrow<HTMLInputElement>(`image_${imgSelectId}`).value = selected;
      const imgSrc = getDomElementOrThrow<HTMLImageElement>(`img-selected-src-${selected}`).src;
      setHtml(
        `imagecontainer_${imgSelectId}`,
        `<img src="${imgSrc}" class="ecommerce-variantconfig-image">`
      );
    } else {
      getDomElementOrThrow<HTMLInputElement>(`image_${imgSelectId}`).value =
        getDomElementOrThrow<HTMLInputElement>(`defaultimg-id-${aspect}`)?.value;
      const defaultUrl = getDomElementOrThrow<HTMLInputElement>(`defaultimg-url-${aspect}`).value;
      setHtml(
        `imagecontainer_${imgSelectId}`,
        `<img src="${defaultUrl}" class="ecommerce-variantconfig-image">`
      );
    }
    closeModalLast();
    toastSuccess("Variant image now set");
  }

  if (mode === "multi") {
    getDomElementOrThrow<HTMLInputElement>(`multi_image_${aspect}`).value = selected;
    const imgSrc = getDomElementOrThrow<HTMLImageElement>(`img-selected-src-${selected}`).src;
    setHtml(
      `multi_target_${aspect}`,
      `<img id="multi_image_src_${aspect}" src="${imgSrc}" class="ecommerce-variantconfig-image2">`
    );
    closeModalLast();
    toastSuccess("Variant image now set");
  }
}

/**
 * Update product images sort order after drag and drop
 */
export function updateproductimagessort(aspect: string): void {
  let neworder = "";

  for (const prevEl of queryAll<HTMLElement>(`.addproduct-img-selected-${aspect}`)) {
    const the_id = prevEl.id;
    if (!the_id) continue;

    const raw_id = the_id.replace("preview-", "");
    if (neworder) neworder += ",";
    neworder += raw_id;
  }

  getDomElementOrThrow<HTMLInputElement>(`addproduct_images_${aspect}`).value = neworder;
  setdefaultimage(aspect);
}

/**
 * Set the default image for a given aspect
 */
export function setdefaultimage(aspect: string): void {
  let defaultimage_id = "";
  let defaultimage_url = "";

  for (const prevEl of queryAll<HTMLElement>(`.addproduct-img-selected-${aspect}`)) {
    const the_id = prevEl.id;
    if (!the_id) continue;

    const raw_id = the_id.replace("preview-", "");
    if (!defaultimage_id) {
      defaultimage_id = raw_id;
      defaultimage_url =
        getDomElementOrThrow<HTMLImageElement>(`interior-source-${raw_id}`).src ?? "";
    }
  }

  getDomElementOrThrow<HTMLInputElement>(`defaultimg-id-${aspect}`).value = defaultimage_id;
  getDomElementOrThrow<HTMLInputElement>(`defaultimg-url-${aspect}`).value = defaultimage_url;

  for (const slot of queryAll<HTMLElement>(`span.set-defaultimg-${aspect}`)) {
    if (!slot.id) continue;
    slot.innerHTML = `<img src="${defaultimage_url}" class="ecommerce-variantconfig-image">`;
  }
}

/**
 * Handle product image selection in modal
 */
export function addproductimageselect(id: string): void {
  const selectMode = getDomElement<HTMLInputElement>("img-select-mode")?.value;

  if (selectMode === "single") {
    const deselect_id = getDomElement<HTMLInputElement>("img-select-selected")?.value;
    if (deselect_id !== "") {
      removeClass(`modal-addproducti-${deselect_id}`, "modal-addproducti-selected");
      getDomElementOrThrow<HTMLInputElement>(`img-selected-${deselect_id}`).value = "0";
      getDomElementOrThrow<HTMLInputElement>("img-select-selected").value = "";
    }
    if (deselect_id !== id) {
      addClass(`modal-addproducti-${id}`, "modal-addproducti-selected");
      getDomElementOrThrow<HTMLInputElement>(`img-selected-${id}`).value = "1";
      getDomElementOrThrow<HTMLInputElement>("img-select-selected").value = id;
    }
  }

  if (selectMode === "multi") {
    if (getDomElement<HTMLInputElement>(`img-selected-${id}`)?.value === "1") {
      removeClass(`modal-addproducti-${id}`, "modal-addproducti-selected");
      getDomElementOrThrow<HTMLInputElement>(`img-selected-${id}`).value = "0";
    } else {
      addClass(`modal-addproducti-${id}`, "modal-addproducti-selected");
      getDomElementOrThrow<HTMLInputElement>(`img-selected-${id}`).value = "1";
    }
  }
}

registerActions({
  setdefaultimage,
});
